import { Injectable, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  UntypedFormControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  distinctUntilChanged,
} from 'rxjs';
import { startWith } from 'rxjs/operators';

import { ModifyForm } from './modify-form.model';

@Injectable()
export abstract class BaseForm<TDataView extends Object, TDto extends Object>
  extends ModifyForm
  implements OnDestroy
{
  get isReady$(): Observable<boolean> {
    return this._isReady$.asObservable().pipe(distinctUntilChanged());
  }

  get isReady(): boolean {
    return this._isReady$.value;
  }

  get id$(): Observable<number> {
    return this._id$.asObservable();
  }

  get controlNames(): string[] {
    return this.formParameter.controlNames.map((controlName) =>
      String(controlName)
    );
  }

  get title(): string {
    return this._title$.value;
  }

  protected onReady: () => void;

  private readonly _id$ = new BehaviorSubject<number>(0);
  private readonly _isReady$ = new BehaviorSubject<boolean>(false);
  protected readonly _unsubscribeAll$ = new Subject<void>();
  private readonly _title$ = new BehaviorSubject<string>('');

  constructor(
    private readonly formParameter: {
      readonly controlNames: Array<keyof TDataView>;
      readonly idSelector: (data: TDataView) => number;
      readonly validators?: Partial<Record<keyof TDataView, ValidatorFn[]>>;
      readonly asyncValidators?: Partial<
        Record<keyof TDataView, AsyncValidatorFn[]>
      >;
      readonly errorHandlers: ((control: AbstractControl) => string)[];
      readonly titleBuilder: {
        fields: (keyof TDataView)[];
      };
    }
  ) {
    super({});
  }

  validate(): void {
    this.updateValueAndValidityAll();
    this.markAllAsTouched();
  }

  getControlError(control: UntypedFormControl): string {
    if (!control) {
      return null;
    }

    let result: string = null;
    const handlers: ((control: UntypedFormControl) => string)[] = [
      this.getBackendError,
      ...(this.formParameter.errorHandlers || []),
    ];

    if (control.hasValidator(Validators.required)) {
      handlers.push(this.getRequiredError);
    }

    handlers.some((handler) => {
      const handlerResult = handler(control);
      if (handlerResult) {
        result = handlerResult;
        return true;
      }
    });

    return result;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  toDto(): TDto {
    const dto = this.controlNames.reduce((prev, controlName) => {
      const control = this[String(controlName)] as AbstractControl;

      if (!control) {
        throw Error(
          `Field ${String(controlName)} should be of AbstractControl type`
        );
      }

      return { ...prev, [controlName]: control.value };
    }, {} as TDto);

    return dto;
  }

  setData(data: TDataView): void {
    if (data === null) {
      this._isReady$.next(false);
      return;
    }

    if (this._isReady$.value) {
      this.updateFormControls(data);
    } else {
      this.setupFormControls(data);
    }
  }

  private updateFormControls(data: TDataView): void {
    this._id$.next(this.formParameter.idSelector(data));
    this.controlNames.forEach((controlName) =>
      (this[String(controlName)] as AbstractControl)?.setValue(
        data[controlName]
      )
    );
  }

  private setupFormControls(data: TDataView): void {
    this._id$.next(this.formParameter.idSelector(data));
    this.controlNames.forEach((controlName) => {
      if (!this.hasOwnProperty(controlName)) {
        throw Error(`Form field ${controlName} is not declared in the form`);
      }
      const validators: ValidatorFn[] =
        (this.formParameter.validators &&
          this.formParameter.validators[controlName]) ||
        [];

      const asyncValidators: AsyncValidatorFn[] =
        (this.formParameter.asyncValidators &&
          this.formParameter.asyncValidators[controlName]) ||
        [];

      this[controlName] = new FormControl(
        data[controlName],
        validators,
        asyncValidators
      );
    });

    this.registerControls();
    this.setupTitle();
    this._isReady$.next(true);

    if (this.onReady) {
      this.onReady();
    }
  }

  private registerControls(): void {
    this.controlNames.forEach((controlName) =>
      this.registerControl(String(controlName), this[String(controlName)])
    );
  }

  private setupTitle(): void {
    if (this.formParameter.titleBuilder?.fields?.length > 0) {
      const fields$ = this.formParameter.titleBuilder.fields.map(
        (fieldName) => {
          const strFieldName = String(fieldName);
          return this.get(strFieldName).valueChanges.pipe(
            startWith(this.get(strFieldName).value)
          );
        }
      );

      combineLatest(fields$).subscribe((values) => {
        this._title$.next(values.join(' '));
      });
    }
  }
}
